import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Button } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useStayPlanStatus } from "@/hooks/useStayPlanStatus";
import { useStayDailyRates } from "@/hooks/useStayDailyRates";
import { filterStayPlanAlertsForComanda, planChargeIsInComanda, type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import type { PanelPet } from "../panel/panelModel";
import { DailyObservationsSection } from "./DailyObservationsSection";
import { PetHistorySection } from "./PetHistorySection";
import { StayActivitiesSection } from "./StayActivitiesSection";
import { StayCancelActions } from "./StayCancelActions";
import { StayComandaSection } from "./StayComandaSection";
import { StayMediaSection } from "./StayMediaSection";
import { StayPetDialog } from "./StayPetDialog";
import { StayPlanCoverageStep, type StayPlanCoverageChoice } from "./StayPlanCoverageStep";
import { StayPlanAlertList } from "../plans/StayPlanAlerts";
import { useCheckInReservation } from "./useCheckInReservation";
import { useStayComanda } from "./useStayComanda";

export interface CheckInDialogProps {
  pet: PanelPet;
  isoDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal de check-in do painel do dia: reaproveita a casca de `StayPetDialog`
 * e todas as seções construídas nas Tasks 1-5. Confirmar grava `checked_in`
 * no dia da estadia, promove o hold do plano (se houver) e fecha o modal — o
 * card some da fila de check-in sozinho porque `useCheckInReservation` invalida
 * o painel.
 */
export function CheckInDialog({ pet, isoDate, open, onOpenChange }: CheckInDialogProps) {
  const { t } = useTranslation("daycareHotel");
  const checkIn = useCheckInReservation();
  const [coverageChoice, setCoverageChoice] = useState<StayPlanCoverageChoice | null>(null);

  const module: StayPlanModule = pet.nature === "hotel" ? "hotel" : "daycare";
  const { status: planStatus } = useStayPlanStatus({ module, petId: pet.petId, date: isoDate });
  const dailyRates = useStayDailyRates();
  const comanda = useStayComanda(pet.reservationId);

  // Saldo esgotado: o plano do pet existe, mas não cobre mais esta diária.
  const isExhausted = planStatus !== null && (
    planStatus.contract.kind === "day_package"
      ? (planStatus.contract.remainingDays ?? 0) <= 0
      : planStatus.balance.effective === 0
  );
  const needsCoverageChoice = isExhausted && coverageChoice === null;

  const confirm = async () => {
    if (needsCoverageChoice) return;
    const coverage: StayPlanCoverageChoice = isExhausted ? (coverageChoice ?? "avulso") : (planStatus ? "plan" : "avulso");
    await checkIn.mutateAsync({
      stayDayId: pet.stayDayId,
      module,
      reservationId: pet.reservationId,
      usedOn: isoDate,
      contractId: planStatus?.contract.id ?? null,
      coverage,
    });
    toast({ title: t("panel.checkInDone") });
    onOpenChange(false);
  };

  return (
    <StayPetDialog
      pet={pet}
      isoDate={isoDate}
      open={open}
      onOpenChange={onOpenChange}
      title={t("panel.checkInTitle", { name: pet.petName })}
      petProfileAlert={planStatus ? (
        <StayPlanAlertList
          alerts={filterStayPlanAlertsForComanda(
            planStatus.alerts,
            planChargeIsInComanda({
              planName: planStatus.contract.planName,
              membershipLineLabel: t("planContract.membershipLine"),
              lines: comanda.data?.lines ?? [],
            }),
          )}
          nextDueOn={planStatus.contract.nextDueOn}
          planName={planStatus.contract.planName}
          petId={planStatus.contract.petId}
          contractId={planStatus.contract.id}
          day={isoDate}
        />
      ) : null}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("panel.close")}</Button>
          <Button disabled={checkIn.isPending || needsCoverageChoice} loading={checkIn.isPending} onClick={confirm}>
            <Check aria-hidden="true" size={18} /> {t("panel.confirmPresence")}
          </Button>
        </div>
      }
    >
      {needsCoverageChoice ? (
        <StayPlanCoverageStep
          dailyRate={dailyRates.data?.[module === "hotel" ? "hotelNightlyRate" : "daycareDailyRate"] ?? null}
          onChoose={setCoverageChoice}
          onContractPlan={() => toast({ title: t("planCoverage.contractPlanHint") })}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <StayActivitiesSection petId={pet.petId} day={isoDate} canComplete />
        <DailyObservationsSection reservationId={pet.reservationId} petId={pet.petId} petName={pet.petName} day={isoDate} />
      </div>
      <StayMediaSection reservationId={pet.reservationId} petId={pet.petId} day={isoDate} />
      <StayComandaSection reservationId={pet.reservationId} />
      <StayCancelActions
        reservationId={pet.reservationId}
        stayDayId={pet.stayDayId}
        module={module}
        stayDate={isoDate}
        stayDayStatus={pet.status}
        hasFutureReservations={Boolean(pet.lastStayDate && pet.lastStayDate > isoDate)}
        onCancelled={() => onOpenChange(false)}
      />
    </StayPetDialog>
  );
}
